import { env } from '@/env'

import packageJson from '../../package.json'

/**
 * Runtime mode (issue 12). Three ORTHOGONAL axes, kept explicitly separate so a
 * flag never conflates "where it runs" with "what's faked":
 *
 *   1. `stage`               — the one mutually-exclusive deployment target.
 *   2. `isMockingEnabled`    — data layer served by MSW (dev:mock + demo build).
 *   3. `isDemoPresentation`  — additive demo UI (route guards, CTA, pruned nav…).
 *
 * Mocking and demo-presentation are independent gates, NOT the same boolean:
 * `dev:mock` mocks data with the full product UI; the demo build does both.
 *
 * Invariant: mocking must never ride the `prod` stage. `vite.config.ts` fails
 * the BUILD on that misconfig; the throw below is the runtime backstop so a
 * mis-provisioned bundle refuses to boot instead of shipping fake data as real.
 */
export type RuntimeStage = 'local' | 'dev' | 'staging' | 'prod'

/** The deployment target this build runs against (VITE_TARGET_STAGE). */
export const stage: RuntimeStage = env.VITE_TARGET_STAGE

/**
 * Is running on a local machine (vite dev server — `import.meta.env.DEV`).
 * Distinct from `stage === 'local'`, which is the deploy-target default.
 */
export const isDEV = import.meta.env.DEV

/**
 * Is running on a production environment (https://air.bitlyftsecurity.com)
 */
export const isPRODDeploy = stage === 'prod'

/**
 * Is running on a development environment (https://dev-air.bitlyftsecurity.com)
 */
export const isDEVDeploy = stage === 'dev'

/**
 * Single data-layer gate for "everything is mocked" (ADR 0006 decision 1):
 * `dev:mock` and the demo build are one offline-mock foundation, so anything
 * that switches on mocking (MSW boot, telemetry skip) checks this — not the
 * `VITE_USE_MOCK_DATA` / `VITE_DEMO` inputs individually.
 */
export const isMockingEnabled = env.VITE_USE_MOCK_DATA || env.VITE_DEMO

/**
 * Additive demo *presentation* layer (ADR 0006): route guards, sidebar pruning
 * (no Graylog / multi-tenant), CTA + Demo-Mode indicator, hidden Noah, Clerk
 * stub. DELIBERATELY distinct from `isMockingEnabled` — presentation is
 * demo-build-only, whereas mocking also covers `dev:mock` (full product UI on
 * fake data). Set by the demo build (`VITE_DEMO`).
 */
export const isDemoPresentation = env.VITE_DEMO

// Runtime backstop for the build-time invariant in `vite.config.ts` (issue 12):
// the mock layer must never ship on the prod stage. If a bundle somehow reaches
// prod with mocking on, refuse to boot rather than serve seeded fake data.
if (isMockingEnabled && stage === 'prod') {
	throw new Error(
		'Runtime invariant violated: mocking (VITE_USE_MOCK_DATA/VITE_DEMO) is enabled on the prod stage.',
	)
}

/**
 * The version of the app
 */
export const appVersion = packageJson.version

/**
 * The URL of the API
 */
export const API_URL = env.VITE_AIR_API_URL

/**
 * Base URL of the AIR® AI (Noah) backend. Separate service from the main AIR®
 * REST API — Noah lives on its own hostname (e.g. `ai.dev1.bitlyftsecurity.com`
 * or `http://localhost:5001` for local). Required env so a missing config
 * fails at boot instead of silently 404'ing against the AIR® REST host.
 */
export const AI_API_URL = env.VITE_AI_API_URL
